import { Add, Check, Close, Delete, Edit, Search as SearchIcon } from '@mui/icons-material';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    IconButton,
    InputAdornment,
    Pagination,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ESkillType } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import changeMapSkill from '@src/APIs/graphQl/Skill/changeMapSkill';
import createAndMapSkill from '@src/APIs/graphQl/Skill/createAndMapSkill';
import CreateSimpleSkillDialog from '@src/App/Components/Dialogs/CreateSimpleSkillDialog/CreateSimpleSkillDialog';
import EditSimpleSkillDialog from '@src/App/Components/Dialogs/EditSimpleSkillDialog/EditSimpleSkillDialog';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import { useMapSkillDialog } from '@src/App/Components/Dialogs/MapSkillDialog/MapSkillDialog';
import SegmentRateBar from '@src/App/Components/General/FormularElements/formsControls/inputs/SegmentRateBar/SegmentRateBar';
import LoadingBox from '@src/App/Components/General/LoadingBox/LoadingBox';
import SkillOverlay from '@src/App/Components/Overlays/SkillOverlay/SkillOverlay';
import type SkillDetail from '@src/Objects/SkillDetail';
import type { AppState } from '@store/store';
import { Brain, Plus } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import type ISkill from '../../../interfaces/ISkill';

import { ProfileTabHeader } from './ProfileTabHeader';
import { SuggestedSkills, SuggestedSkillsPreview } from './SuggestedSkills';
import { SuperPowersSection } from './SuperPowersSection';

const ITEMS_PER_PAGE = 12;

type SortField = 'name' | 'level' | 'category' | 'skillType';
type SortDirection = 'asc' | 'desc';

interface INewSkill {
    id?: number;
    title: string;
    level: number;
}

/**
 * Represents the SkillsTab component.
 *
 * @return {JSX.Element} The rendered SkillsTab.
 */
export function SkillsTab({ showSuggestionsPage }: { showSuggestionsPage?: boolean }): JSX.Element {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const showErrorDialog = useErrorDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).profileSettings;
    const skillsLangStrings = getLanguageTexts(lang).skills;
    const allSkills = useSelector((state: AppState) => state.company.allSkills);
    const skillCategories = useSelector((state: AppState) => state.company.skillCategories);
    const skills = useSelector((state: AppState) => state.skills);
    const employee = useSelector((state: AppState) => state.employee);
    const offline = useSelector((state: AppState) => state.client.offline);

    // Helper to get category title from skill ID
    const getCategoryTitle = (skillId: number) => {
        const skill = allSkills.find((s) => s.id === skillId);
        if (!skill) return 'Unbekannte Kategorie';
        const category = skillCategories.find((cat) => cat.id === skill.categoryId);
        return category?.title || 'Unbekannte Kategorie';
    };

    // Helper to get skill type from skill ID
    const getSkillType = (skillId: number) => {
        const skillDetail = skills.find((s) => s.id === skillId);
        return skillDetail?.skillType || null;
    };

    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [newSkill, setNewSkill] = useState<INewSkill>({ title: '', level: 0 });
    const [addingSkills, setAddingSkills] = useState<string[]>([]);
    const [sortField, setSortField] = useState<SortField>('level');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    // Dialog hooks
    const mapSkillDialog = useMapSkillDialog();
    const [createSkillDialogOpen, setCreateSkillDialogOpen] = useState(false);
    const [editSkillDialogOpen, setEditSkillDialogOpen] = useState(false);
    const [editingSkill, setEditingSkill] = useState<SkillDetail | null>(null);

    if (!employee) {
        return <LoadingBox />;
    }

    if (showSuggestionsPage) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <ProfileTabHeader title="Vorgeschlagene Skills" subtitle="Entdecke neue Skills für dein Profil" />
                <SuggestedSkills />
            </Box>
        );
    }

    // Filter and sort skills
    const filteredSkills = useMemo(() => {
        let skills = [...employee.skills];
        if (searchTerm) {
            skills = skills.filter((skill) => skill.title.toLowerCase().includes(searchTerm.toLowerCase()));
        }
        return skills.sort((a, b) => {
            if (sortField === 'level') {
                const levelDiff = sortDirection === 'desc' ? b.level - a.level : a.level - b.level;
                if (levelDiff !== 0) return levelDiff;
                return a.title.localeCompare(b.title);
            } else if (sortField === 'category') {
                const catA = getCategoryTitle(a.id);
                const catB = getCategoryTitle(b.id);
                const catDiff = sortDirection === 'desc' ? catB.localeCompare(catA) : catA.localeCompare(catB);
                if (catDiff !== 0) return catDiff;
                return a.title.localeCompare(b.title);
            } else if (sortField === 'skillType') {
                const typeA = getSkillType(a.id) || '';
                const typeB = getSkillType(b.id) || '';
                const typeDiff = sortDirection === 'desc' ? typeB.localeCompare(typeA) : typeA.localeCompare(typeB);
                if (typeDiff !== 0) return typeDiff;
                return a.title.localeCompare(b.title);
            } else {
                const nameDiff =
                    sortDirection === 'desc' ? b.title.localeCompare(a.title) : a.title.localeCompare(b.title);
                return nameDiff;
            }
        });
    }, [employee.skills, searchTerm, sortField, sortDirection]);

    // Pagination
    const totalPages = Math.ceil(filteredSkills.length / ITEMS_PER_PAGE);
    const paginatedSkills = filteredSkills.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

    // Available skills for autocomplete
    const availableSkills = allSkills.filter((skill) => !employee.skills.find((empSkill) => empSkill.id === skill.id));

    const handleLevelChange = async (skill: ISkill, newLevel: number) => {
        try {
            await changeMapSkill(skill.id, true, newLevel);
        } catch (error) {
            showErrorDialog(error as Error, 'Failed to change level!');
        }
    };

    const handleDeleteSkill = async (skill: ISkill) => {
        if (!skill.id) return;
        try {
            await changeMapSkill(skill.id, false);
        } catch (error) {
            showErrorDialog(error as Error, skillsLangStrings.errorRemove);
        }
    };

    const handleAddSkill = async () => {
        if (!newSkill.title.trim() || newSkill.level === 0) return;

        setAddingSkills((prev) => [...prev, newSkill.title]);

        try {
            if (newSkill.id) {
                await changeMapSkill(newSkill.id, true, newSkill.level);
            } else {
                await createAndMapSkill(newSkill.title, newSkill.level);
            }
            setNewSkill({ title: '', level: 0 });
        } catch (error) {
            showErrorDialog(error as Error, skillsLangStrings.errorAdd);
        } finally {
            setAddingSkills((prev) => prev.filter((title) => title !== newSkill.title));
        }
    };

    const handleCreateNewSkill = () => {
        setCreateSkillDialogOpen(true);
    };

    const handleSkillCreated = (skill: SkillDetail) => {
        // Skill wurde erfolgreich erstellt - Redux Store wird automatisch aktualisiert
        console.log('Skill erfolgreich erstellt:', skill.title, 'SkillType:', skill.skillType);
    };

    const handleEditSkill = (skillId: number) => {
        const skillDetail = skills.find((s) => s.id === skillId);
        if (skillDetail) {
            setEditingSkill(skillDetail);
            setEditSkillDialogOpen(true);
        }
    };

    const handleSkillUpdated = (skill: SkillDetail) => {
        console.log('Skill erfolgreich aktualisiert:', skill.title);
        setEditingSkill(null);
    };

    // Check if skill is user-created (self-created skills can be edited)
    const isUserCreatedSkill = (skillId: number) => {
        const skillDetail = skills.find((s) => s.id === skillId);
        return skillDetail?.acquisition === 'Selbststudium/Berufserfahrung';
    };

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
        setPage(1);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <ProfileTabHeader title={langStrings.yourSkills} subtitle="Verwalte deine Fähigkeiten und Skills" />

            <SuperPowersSection />

            <SuggestedSkillsPreview />

            <Paper
                elevation={0}
                sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    overflow: 'hidden',
                }}
            >
                {/* Add New Skill */}
                <Box sx={{ px: { xs: 1, md: 3, lg: 4 }, py: { xs: 1, md: 3, lg: 4 }, pt: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                        Skill hinzufügen
                    </Typography>

                    {/* Bestehenden Skill hinzufügen */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            mb: 2,
                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                            bgcolor: alpha(theme.palette.info.main, 0.02),
                            borderRadius: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <SearchIcon sx={{ fontSize: 16, color: theme.palette.info.main }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                                Bestehenden Skill hinzufügen
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                            Wähle einen bestehenden Skill aus deinem Unternehmen aus
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="end">
                            <Autocomplete
                                options={availableSkills}
                                getOptionLabel={(option) => option.title}
                                onChange={(_, value) => {
                                    if (value) {
                                        setNewSkill({ id: value.id, title: value.title, level: 0 });
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} size="small" placeholder="Skill auswählen..." />
                                )}
                                sx={{ flex: 1, minWidth: 200 }}
                                disabled={offline}
                            />
                            <Box sx={{ minWidth: 120 }}>
                                <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                    Level
                                </Typography>
                                <SegmentRateBar
                                    level={newSkill.level}
                                    onChange={(level) => setNewSkill((prev) => ({ ...prev, level }))}
                                    disabled={offline}
                                />
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={handleAddSkill}
                                disabled={
                                    !newSkill.title.trim() ||
                                    newSkill.level === 0 ||
                                    offline ||
                                    addingSkills.includes(newSkill.title)
                                }
                                sx={{ minWidth: 120 }}
                            >
                                {addingSkills.includes(newSkill.title) ? 'Wird hinzugefügt...' : 'Hinzufügen'}
                            </Button>
                        </Stack>
                    </Paper>

                    {/* Neuen Skill erstellen */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                            bgcolor: alpha(theme.palette.success.main, 0.02),
                            borderRadius: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Plus size={16} color={theme.palette.success.main} />
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, color: theme.palette.success.main }}
                                >
                                    Neuen Skill erstellen
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<Plus size={16} />}
                                onClick={handleCreateNewSkill}
                                disabled={offline}
                                sx={{ textTransform: 'none' }}
                            >
                                Neuen Skill erstellen
                            </Button>
                        </Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Erstellt einen komplett neuen Skill für dein Unternehmen
                        </Typography>
                    </Paper>
                </Box>

                {/* Skills List Header */}
                <Box sx={{ px: { xs: 1, md: 3, lg: 4 }, py: { xs: 1, md: 3, lg: 4 }, pt: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        {langStrings.yourSkills} ({filteredSkills.length})
                    </Typography>
                    <TextField
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(1);
                        }}
                        placeholder="In deinen Skills suchen..."
                        variant="outlined"
                        size="small"
                        fullWidth
                        InputProps={{
                            startAdornment: <SearchIcon style={{ marginRight: 8, opacity: 0.6 }} />,
                        }}
                        sx={{ maxWidth: 400 }}
                    />
                </Box>

                {/* Skills List - Mobile vs Desktop */}
                {isMobile ? (
                    <Box sx={{ p: 2 }}>
                        <Stack spacing={1}>
                            {paginatedSkills.map((skill) => (
                                <Paper
                                    key={skill.id}
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                        borderRadius: 2,
                                        '&:hover': {
                                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                                        },
                                    }}
                                >
                                    <Stack spacing={2}>
                                        {/* Skill Name */}
                                        <SkillOverlay skillId={skill.id}>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 500,
                                                    cursor: 'pointer',
                                                    color: theme.palette.primary.main,
                                                    '&:hover': {
                                                        textDecoration: 'underline',
                                                    },
                                                }}
                                            >
                                                {skill.title}
                                            </Typography>
                                        </SkillOverlay>

                                        {/* Level Control */}
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                sx={{ display: 'block', mb: 1, color: 'text.secondary' }}
                                            >
                                                Level {skill.level}/5
                                            </Typography>
                                            <SegmentRateBar
                                                level={skill.level}
                                                onChange={(level) => handleLevelChange(skill, level)}
                                                disabled={offline}
                                            />
                                        </Box>

                                        {/* Action Buttons */}
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            {isUserCreatedSkill(skill.id) && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditSkill(skill.id)}
                                                    disabled={offline}
                                                    color="secondary"
                                                    aria-label={`Skill ${skill.title} bearbeiten`}
                                                    sx={{
                                                        bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.secondary.main, 0.2),
                                                        },
                                                    }}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            )}
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteSkill(skill)}
                                                disabled={offline}
                                                color="primary"
                                                aria-label={`Skill ${skill.title} aus Profil entfernen`}
                                                sx={{
                                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                    '&:hover': {
                                                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                                                    },
                                                }}
                                            >
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    </Box>
                ) : (
                    <TableContainer sx={{ px: { xs: 1, md: 3, lg: 4 }, pb: { xs: 1, md: 3, lg: 4 } }}>
                        <Table sx={{ '& .MuiTableCell-root': { px: { xs: 1, md: 3, lg: 4 } } }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                        <TableSortLabel
                                            active={sortField === 'name'}
                                            direction={sortField === 'name' ? sortDirection : 'asc'}
                                            onClick={() => handleSort('name')}
                                        >
                                            Skill Name
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, width: 140 }}>
                                        <TableSortLabel
                                            active={sortField === 'category'}
                                            direction={sortField === 'category' ? sortDirection : 'asc'}
                                            onClick={() => handleSort('category')}
                                        >
                                            Kategorie
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, width: 100 }}>
                                        <TableSortLabel
                                            active={sortField === 'skillType'}
                                            direction={sortField === 'skillType' ? sortDirection : 'asc'}
                                            onClick={() => handleSort('skillType')}
                                        >
                                            Typ
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600, width: 180 }}>
                                        <TableSortLabel
                                            active={sortField === 'level'}
                                            direction={sortField === 'level' ? sortDirection : 'desc'}
                                            onClick={() => handleSort('level')}
                                        >
                                            Level
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, width: 120 }}>
                                        Aktionen
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedSkills.map((skill) => (
                                    <TableRow
                                        key={skill.id}
                                        sx={{
                                            borderLeft: '2px solid transparent',
                                            '&:hover': {
                                                backgroundColor: `${alpha(theme.palette.primary.main, 0.02)} !important`,
                                                borderLeft: `2px solid ${theme.palette.primary.main}`,
                                            },
                                        }}
                                    >
                                        <TableCell>
                                            <SkillOverlay skillId={skill.id}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 500,
                                                        cursor: 'pointer',
                                                        color: theme.palette.primary.main,
                                                        '&:hover': {
                                                            textDecoration: 'underline',
                                                        },
                                                    }}
                                                >
                                                    {skill.title}
                                                </Typography>
                                            </SkillOverlay>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getCategoryTitle(skill.id)}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                                    borderColor: alpha(theme.palette.primary.main, 0.2),
                                                    fontSize: '0.7rem',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {getSkillType(skill.id) && (
                                                <Chip
                                                    label={getSkillType(skill.id)}
                                                    size="small"
                                                    variant="filled"
                                                    sx={{
                                                        bgcolor:
                                                            getSkillType(skill.id) === ESkillType.HARD_SKILL
                                                                ? alpha(theme.palette.info.main, 0.1)
                                                                : getSkillType(skill.id) === ESkillType.SOFT_SKILL
                                                                  ? alpha(theme.palette.success.main, 0.1)
                                                                  : alpha(theme.palette.warning.main, 0.1),
                                                        color:
                                                            getSkillType(skill.id) === ESkillType.HARD_SKILL
                                                                ? theme.palette.info.main
                                                                : getSkillType(skill.id) === ESkillType.SOFT_SKILL
                                                                  ? theme.palette.success.main
                                                                  : theme.palette.warning.main,
                                                        fontSize: '0.7rem',
                                                    }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                <SegmentRateBar
                                                    level={skill.level}
                                                    onChange={(level) => handleLevelChange(skill, level)}
                                                    disabled={offline}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                {isUserCreatedSkill(skill.id) && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditSkill(skill.id)}
                                                        disabled={offline}
                                                        color="secondary"
                                                        aria-label={`Skill ${skill.title} bearbeiten`}
                                                        title="Skill bearbeiten"
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteSkill(skill)}
                                                    disabled={offline}
                                                    color="primary"
                                                    aria-label={`Skill ${skill.title} aus Profil entfernen`}
                                                    title="Skill entfernen"
                                                >
                                                    <Close fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Empty State */}
                {filteredSkills.length === 0 && (
                    <Box sx={{ p: 6, textAlign: 'center' }}>
                        <Brain size={48} color={theme.palette.text.disabled} style={{ marginBottom: 16 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                            {searchTerm ? 'Keine Skills gefunden' : 'Noch keine Skills'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {searchTerm
                                ? `Keine Skills gefunden für "${searchTerm}"`
                                : 'Fügen Sie Ihren ersten Skill hinzu'}
                        </Typography>
                    </Box>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_, newPage) => setPage(newPage)}
                            color="primary"
                        />
                    </Box>
                )}
            </Paper>

            <CreateSimpleSkillDialog
                open={createSkillDialogOpen}
                onClose={() => setCreateSkillDialogOpen(false)}
                onSkillCreated={handleSkillCreated}
            />

            {editingSkill && (
                <EditSimpleSkillDialog
                    open={editSkillDialogOpen}
                    onClose={() => {
                        setEditSkillDialogOpen(false);
                        setEditingSkill(null);
                    }}
                    onSkillUpdated={handleSkillUpdated}
                    skill={editingSkill}
                />
            )}
        </Box>
    );
}
